/**
 * The subtitle translator (SEO_BRIEF_09 sections 2.3 and 6.2): /subtitle-translator,
 * /translate-srt, /translate-vtt and the ten translated pages that run the first in their language.
 * The page is tools/subtitle_translator.html.
 *
 * THE CHAIN. The file never leaves this tab; its lines of text do.
 *
 *   drop     parse in the worker, with the converter's readers (`retainRaw`, so a line that is
 *            not translated can be written back byte for byte, and `retainBytes`, so every
 *            output is made from a fresh copy)
 *   read     `cueTexts`: each cue's text with its tags, by position (contracts/adapter.md §3.5)
 *   check    5,000 lines and 2 MB of text at most (section 2.3), before anything is sent
 *   send     the distinct texts, numbered, to POST /api/subtitles/translate (lib/gemini.ts),
 *            with "Queued" and "Translating" while it waits and the limit sentence if the
 *            day's allowance is spent
 *   rebuild  `translateCues` on a fresh copy of the file, in the file's own format (or the page's,
 *            or the one picked), or with `pair` and the merge pages' `mergeDocuments` for
 *            "Original and translation in one file"; every timing as it was
 *   save     <file>.<target>.<ext>
 *
 * ONE TRANSLATION PER FILE AND TARGET. The server's answer is kept here while the file is on
 * screen, keyed by the target and the texts sent, so changing the format or the two-language
 * switch rebuilds the file from it and never asks the server again; a target asked for twice is
 * answered from here too. It is kept by the numbers sent and spread over the file's cues each time
 * (`translationCache`), never as cue positions. The server keeps its own 30-day copy, so the same
 * file translated again (dropped again, or in another tab) costs nothing either.
 *
 * ONE TRANSLATION IN FLIGHT. A new file, the file read again (encoding, frame rate) or another
 * target aborts it (`translationFlight`): nothing it says afterwards reaches the page, and
 * Translate is offered again at once.
 *
 * THE BYTES EXIST BEFORE DOWNLOAD IS ENABLED, as on every tool page: saving after an await is
 * what makes a download fail silently in Safari.
 *
 * Counted (lib/usage.ts): `loaded` or `failed` once per file, `downloaded` per save, and section
 * 2.7's `subtitle_translate {target, cues, format}` per translation the server makes, and
 * `gemini_limit_hit {feature: 'subtitles', scope}` when the allowance is spent. No text, no name.
 */

import type {
  DocumentId,
  Framerate,
  LossEntry,
  WriteFormat,
} from '../../../../../contracts/cue-model.ts';
import { stableHash } from '../../../../../src/core/ids/allocator.ts';
import type { CueTextRow, ParsedInfo } from '../../../../../src/core/protocol.ts';
import { labelled, opsFromValues } from '../../../../../src/tools/ops-from-values.ts';
import { ApiError } from '../lib/api.ts';
import { data, on, qs } from '../lib/dom.ts';
import { downloadChoices, renderOutcome } from '../lib/download-row.ts';
import { basenameOf, saveBytes, slugifyBasename } from '../lib/download.ts';
import { classify, extensionOf, mountDropzone } from '../lib/dropzone.ts';
import { fileSize, shortFilename } from '../lib/format.ts';
import {
  GeminiLimitError,
  limitMessage,
  SUBTITLE_LIMITS,
  type TaskEvents,
  translateSubtitles,
} from '../lib/gemini.ts';
import { type PageLang, pageLang, t } from '../lib/i18n.ts';
import { parseFramerate } from '../lib/option-panel.ts';
import { showProblem, showState } from '../lib/states.ts';
import { PICKER, TARGET } from '../lib/translate-target.ts';
import { countUse, failedFormat, usageCounter, usageFormat } from '../lib/usage.ts';
import { isVersionMismatch, WorkerFailure, worker } from '../lib/worker.ts';
import { downloadWriteOptions, writeFramerate } from '../lib/write-options.ts';

/**
 * Section 2.3: 5,000 lines and "2 MB of text" a file, the server's own numbers
 * (contracts/api.md §3.11.4: 2 MiB of UTF-8), so the page refuses exactly what the server would.
 */
export const MAX_CUES = SUBTITLE_LIMITS.cues;
export const MAX_TEXT_BYTES = SUBTITLE_LIMITS.textBytes;

/** Lines the page shows from the file as read, so a wrong encoding is seen before anything is sent. */
const PREVIEW_CUES = 6;

/** The writers that put an untouched line back as the file had it (`useRawPassthrough`). */
const PASSTHROUGH: ReadonlySet<string> = new Set(['srt', 'ass', 'ssa']);

/** The skips that leave words on screen untranslated: counted in the "lines couldn't be" notice. */
const LEFT_AS_THEY_WERE: ReadonlySet<string> = new Set(['drawing', 'fragile', 'styled']);

/* ── pure: what to send, what came back ───────────────────────────────── */

/** `es` from `es-MX`, `zh` from `zh-Hant`: a tag's language, lower case. */
export function primaryLanguage(tag: string | null | undefined): string | null {
  if (typeof tag !== 'string' || tag.trim() === '') return null;
  return tag.trim().split(/[-_]/, 1)[0]?.toLowerCase() || null;
}

/** The picker's Traditional Chinese, the one target with a script of its own. */
const TRADITIONAL = 'zh-Hant';

/** A tag that reads Traditional Chinese: zh-Hant (zh-Hant-TW too), zh-TW, zh-HK and zh-MO. */
export function isTraditional(tag: string | null | undefined): boolean {
  return typeof tag === 'string' && /^zh[-_](?:hant|tw|hk|mo)\b/i.test(tag.trim());
}

/**
 * The language a track's lines are sent as (`SendPlan.source`): `zh-Hant` for any Traditional
 * tag, so the model knows the script it reads, else the tag's language (`es` from `es-MX`).
 */
export function sourceLanguage(tag: string | null | undefined): string | null {
  return isTraditional(tag) ? TRADITIONAL : primaryLanguage(tag);
}

/**
 * True when a track already in `language` needs no translating into `target`: the same language
 * (a region does not matter), but Chinese by script, as the server's `same_language` has it. A
 * Traditional track (zh-TW, zh-HK, zh-MO, zh-Hant) is not "already" in Chinese (Simplified), and
 * only a Traditional track is already in Traditional: zh, zh-Hans and zh-CN are not.
 */
export function alreadyIn(language: string | null, target: string): boolean {
  if (target === TRADITIONAL) return isTraditional(language);
  if (target.includes('-')) return false;
  if (target === 'zh' && isTraditional(language)) return false;
  return primaryLanguage(language) === target;
}

export interface SendPlan {
  /** The distinct texts, numbered from 0: the request's `cues`. */
  readonly send: readonly { readonly i: number; readonly t: string }[];
  /** For each number sent, the positions of the cues that carry that text. */
  readonly positions: readonly (readonly number[])[];
  /** Cues with words that are not sent and stay as they are (a sign, a drawing among words). */
  readonly kept: number;
  /** Cues on a track already in the target, not sent. */
  readonly inTarget: number;
  /** The text sent, in UTF-8 bytes. */
  readonly textBytes: number;
  /** The one language every sent cue's track declares (`sourceLanguage`), or null. */
  readonly source: string | null;
}

/**
 * What to send for `target`: each text once (a chorus, a "♪", a "[Music]" repeated across a film
 * costs one line), never the cues the worker would not send, nor a track already in the target.
 */
export function planRequest(
  cues: readonly CueTextRow[],
  tracks: readonly { readonly id: string; readonly language: string | null }[],
  target: string,
): SendPlan {
  const language = new Map(tracks.map((tr) => [tr.id, tr.language]));
  const numbers = new Map<string, number>();
  const send: { i: number; t: string }[] = [];
  const positions: number[][] = [];
  const sources = new Set<string | null>();
  let kept = 0;
  let inTarget = 0;
  let textBytes = 0;
  const encoder = new TextEncoder();
  for (const cue of cues) {
    if (cue.text === null) {
      if (cue.skip !== null && LEFT_AS_THEY_WERE.has(cue.skip)) kept += 1;
      continue;
    }
    const trackLanguage = language.get(cue.trackId) ?? null;
    if (alreadyIn(trackLanguage, target)) {
      inTarget += 1;
      continue;
    }
    sources.add(sourceLanguage(trackLanguage));
    let n = numbers.get(cue.text);
    if (n === undefined) {
      n = send.length;
      numbers.set(cue.text, n);
      send.push({ i: n, t: cue.text });
      positions.push([]);
      textBytes += encoder.encode(cue.text).byteLength;
    }
    (positions[n] as number[]).push(cue.i);
  }
  const only = sources.size === 1 ? [...sources][0] : null;
  return {
    send,
    positions,
    kept,
    inTarget,
    textBytes,
    source: only !== undefined && only !== null && TARGET.test(only) ? only : null,
  };
}

/** Why a file is refused before anything is sent (section 2.3), or null. */
export function overLimit(
  cueCount: number,
  textBytes: number,
): { readonly label: 'too-many' | 'too-much'; readonly count: number } | null {
  if (cueCount > MAX_CUES) return { label: 'too-many', count: cueCount };
  if (textBytes > MAX_TEXT_BYTES) return { label: 'too-much', count: textBytes };
  return null;
}

export interface Translation {
  readonly target: string;
  /** Translated text by cue position, for `translateCues`. */
  readonly texts: readonly { readonly i: number; readonly text: string }[];
  /** Cues sent and not translated: left out by the model, or answered empty. */
  readonly missing: number;
  /** Cues the worker would not send (`SendPlan.kept`). */
  readonly kept: number;
}

/** The server's answer for one target and one set of texts: by number sent, never by cue. */
export interface Answer {
  readonly cues: readonly { readonly i: number; readonly t: string }[];
  readonly missing: readonly number[];
}

/** The answer spread back over every position that carried each text. */
export function translationFrom(plan: SendPlan, target: string, answer: Answer): Translation {
  const missing = new Set(answer.missing);
  const byNumber = new Map<number, string>();
  for (const cue of answer.cues) {
    if (missing.has(cue.i) || typeof cue.t !== 'string' || cue.t.trim() === '') continue;
    byNumber.set(cue.i, cue.t);
  }
  const texts: { i: number; text: string }[] = [];
  let left = 0;
  for (const sent of plan.send) {
    const where = plan.positions[sent.i] ?? [];
    const text = byNumber.get(sent.i);
    if (text === undefined) {
      left += where.length;
      continue;
    }
    for (const i of where) texts.push({ i, text });
  }
  texts.sort((a, b) => a.i - b.i);
  return { target, texts, missing: left, kept: plan.kept };
}

/** The page's own key for a translation: the target and exactly the texts sent. */
export function translationKey(target: string, plan: SendPlan): string {
  return `${target}:${plan.send.length}:${stableHash(JSON.stringify(plan.send))}`;
}

export interface TranslationCache {
  /** The translation `plan` already has for `target`, spread over its own cues; else null. */
  find(plan: SendPlan, target: string): Translation | null;
  /** Keep the server's `answer` for `plan`'s texts, and spread it over `plan`'s cues. */
  keep(plan: SendPlan, target: string, answer: Answer): Translation;
  clear(): void;
}

/**
 * The answers the page has, by `translationKey`. What is kept is the server's answer, numbered as
 * the texts were sent, and it is spread over the cues of the plan it is used for each time: the
 * same texts at other positions (a second file with a comment line first) get their own
 * positions, never those of the file that asked first.
 */
export function translationCache(): TranslationCache {
  const answers = new Map<string, Answer>();
  return {
    find(plan, target) {
      const answer = answers.get(translationKey(target, plan));
      return answer === undefined ? null : translationFrom(plan, target, answer);
    },
    keep(plan, target, answer) {
      answers.set(translationKey(target, plan), answer);
      return translationFrom(plan, target, answer);
    },
    clear() {
      answers.clear();
    },
  };
}

export interface TranslationFlight {
  /** True while a translation is in flight: Translate is not offered. */
  readonly busy: boolean;
  /**
   * Start `ask`, cancelling the one before. `ask` gets `events` with the flight's own `signal`, to
   * pass on to lib/gemini.ts. Resolves to its answer, or to null once it is cancelled: a
   * cancelled ask never reports progress, never throws (its AbortError least of all), and its
   * answer, if one still arrives, is dropped.
   */
  run<T>(events: TaskEvents, ask: (on: TaskEvents) => Promise<T>): Promise<T | null>;
  /** Abort the ask in flight, if any. `busy` is false at once, so Translate is offered again. */
  cancel(): void;
}

/**
 * The one translation the page has in flight. A new file, the file read again (another encoding
 * or frame rate) and another target each cancel it (`cancel`): the request and its polling are
 * aborted, and nothing it says afterwards reaches the page, so a late "Translating the lines…"
 * never lands on the next file and a Spanish answer never shows under a French picker.
 */
export function translationFlight(): TranslationFlight {
  let current: AbortController | null = null;
  return {
    get busy() {
      return current !== null;
    },
    async run<T>(events: TaskEvents, ask: (on: TaskEvents) => Promise<T>): Promise<T | null> {
      current?.abort();
      const mine = new AbortController();
      current = mine;
      const { signal } = mine;
      const on: TaskEvents = {
        signal,
        queued: () => {
          if (!signal.aborted) events.queued?.();
        },
        running: () => {
          if (!signal.aborted) events.running?.();
        },
      };
      try {
        const answer = await ask(on);
        return signal.aborted ? null : answer;
      } catch (err) {
        if (signal.aborted) return null;
        throw err;
      } finally {
        // Only its own: a flight started since is still in the air.
        if (current === mine) current = null;
      }
    },
    cancel() {
      current?.abort();
      current = null;
    },
  };
}

/** `film.en.srt` → `film`: a language tag at the end of a name is the old language's. */
function withoutLanguage(base: string, known: ReadonlySet<string>): string {
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return base;
  const tail = base.slice(dot + 1);
  const lower = tail.toLowerCase();
  const tagged =
    known.has(lower) ||
    known.has(tail) ||
    /^[a-z]{2}-[a-z]{2}$/i.test(tail) ||
    /^zh-han[st]$/i.test(tail);
  return tagged ? base.slice(0, dot) : base;
}

/**
 * `<file>.<target>.<ext>`: what players look for beside a video (film.es.srt). A language the
 * name already ends with is replaced, so film.en.srt becomes film.es.srt, not film.en.es.srt.
 * `written` is the worker's own name for the file, whose extension is the format's.
 */
export function downloadName(
  sourceName: string,
  target: string,
  written: string,
  known: ReadonlySet<string> = new Set(),
): string {
  const dot = written.lastIndexOf('.');
  const ext = dot > 0 ? written.slice(dot) : '';
  const base = withoutLanguage(slugifyBasename(basenameOf(sourceName)), known);
  return `${base}.${target}${ext}`;
}

/**
 * The format a translation is saved in first: the page's own (SRT on /translate-srt), else the
 * file's, else the first the page writes.
 */
export function defaultFormat(
  pageFormat: string | null,
  sourceFormat: string,
  writes: readonly string[],
): string | undefined {
  if (pageFormat !== null && writes.includes(pageFormat)) return pageFormat;
  if (writes.includes(sourceFormat)) return sourceFormat;
  return writes[0];
}

/**
 * The save-as choices: every format the page writes, but MicroDVD and SCC, which count in frames,
 * only when the file came in as that format and so carries the rate to write them at.
 */
export function saveChoices(writes: readonly string[], sourceFormat: string): readonly string[] {
  return writes.filter((f) => (f === 'sub' || f === 'scc' ? f === sourceFormat : true));
}

/**
 * The picker's first choice: the page's own target (a translated page's language), else the
 * browser's first language the picker offers, else what the picker already shows.
 */
export function defaultTarget(
  pageTarget: string | null,
  languages: readonly string[],
  offered: readonly string[],
): string | null {
  if (pageTarget !== null && offered.includes(pageTarget)) return pageTarget;
  for (const tag of languages) {
    const lower = tag.toLowerCase();
    // zh-TW, zh-HK and zh-Hant read Traditional; every other Chinese, Simplified.
    if (/^zh-(tw|hk|mo|hant)/.test(lower) && offered.includes('zh-Hant')) return 'zh-Hant';
    const code = primaryLanguage(lower);
    if (code !== null && offered.includes(code)) return code;
  }
  return null;
}

/** A language's name in the page's language ("español" on a Spanish page), else `fallback`. */
export function languageName(code: string, lang: PageLang, fallback: string): string {
  try {
    const name = new Intl.DisplayNames([lang], { type: 'language' }).of(code);
    if (name && name !== code) return name;
  } catch {
    /* an engine without DisplayNames, or a code it refuses */
  }
  return fallback;
}

/**
 * The notice for lines left in their own language (section 2.3: "3 lines couldn't be translated
 * and are left in Spanish."). The language when the file says it, else "as they were".
 */
export function leftNotice(count: number, language: string | null, lang: PageLang): string {
  if (count <= 0) return '';
  const n = count.toLocaleString(lang === 'en' ? 'en-US' : lang);
  if (language === null) {
    return count === 1
      ? t("1 line couldn't be translated and is left as it was.", lang)
      : t("{n} lines couldn't be translated and are left as they were.", lang).replace('{n}', n);
  }
  return (
    count === 1
      ? t("1 line couldn't be translated and is left in {language}.", lang)
      : t("{n} lines couldn't be translated and are left in {language}.", lang).replace('{n}', n)
  ).replace('{language}', language);
}

/* ── the page ─────────────────────────────────────────────────────────── */

type Label = 'queued' | 'running' | 'too-many' | 'too-much' | 'no-text';

interface Source {
  readonly file: File;
  readonly info: ParsedInfo;
  readonly cues: readonly CueTextRow[];
  /** The encoding the file was read in: every fresh copy reads it the same way. */
  readonly encoding: string;
}

export function mountSubtitleTranslatePage(): void {
  const root = qs<HTMLElement>('[data-subtrans]');
  if (!root) return;
  const tool = data(root, 'usage-tool') ?? 'subtitle-translator';
  const pageTarget = data(root, 'target-default');
  const pageFormat = data(root, 'to-format');
  const lang = pageLang();

  const region = qs<HTMLElement>('#banner-region', root);
  const picker = qs<HTMLSelectElement>(PICKER, root);
  const parsed = qs<HTMLElement>('[data-parsed-preview]', root);
  const preview = qs<HTMLElement>('[data-subtrans-preview]', root);
  const encodingSelect = qs<HTMLSelectElement>('[data-encoding-override]', root);
  const encodingPanel = qs<HTMLDetailsElement>('[data-encoding-panel]', root);
  const fpsField = qs<HTMLElement>('[data-fps-field]', root);
  const fpsInput = qs<HTMLInputElement>('[data-fps-input]', root);
  const run = qs<HTMLButtonElement>('[data-subtrans-run]', root);
  const status = qs<HTMLElement>('[data-subtrans-status]', root);
  const notice = qs<HTMLElement>('[data-subtrans-notice]', root);
  const result = qs<HTMLElement>('[data-subtrans-result]', root);
  const formatSelect = qs<HTMLSelectElement>('[data-write-format-select]', root);
  const both = qs<HTMLInputElement>('[data-subtrans-both]', root);
  const outcome = qs<HTMLElement>('[data-download-outcome]', root);
  const download = qs<HTMLButtonElement>('[data-download]', root);
  const host = qs<HTMLElement>('[data-dropzone-host]', root);
  if (!picker || !run || !host) return;
  // Narrowed once, for the functions below, which TypeScript does not narrow by the line above.
  const targetSelect: HTMLSelectElement = picker;
  const runButton: HTMLButtonElement = run;

  const usage = usageCounter(tool);
  const client = worker();
  const offered = [...picker.options].map((o) => o.value);
  const known = new Set(offered.map((c) => c.toLowerCase()));
  /** Every format the page writes, as the template lists them; the choices are made from these. */
  const formatOptions = formatSelect ? [...formatSelect.options] : [];
  const writes = formatOptions.map((o) => o.value);

  const first = defaultTarget(
    pageTarget,
    typeof navigator === 'undefined' ? [] : [...navigator.languages],
    offered,
  );
  if (first !== null) picker.value = first;

  let source: Source | null = null;
  /** The server's answers for the file on screen (`translationCache`); a new file starts empty. */
  const translations = translationCache();
  /** The translation the result section shows, and the file it was made for. */
  let shown: { readonly translation: Translation; readonly source: Source } | null = null;
  /** The file Download saves, or null while there is none for what is on screen. */
  let output: { bytes: Uint8Array | string; filename: string; format: WriteFormat } | null = null;
  /** Bumped by every new file, encoding, rate, target and option, so a late answer is dropped. */
  let generation = 0;
  /** The translation in flight (`translationFlight`): cancelled by whatever makes it stale. */
  const flight = translationFlight();
  let framerate: Framerate | null = null;
  /** The file being read, for the frame-rate answer to read again. */
  let pending: File | null = null;

  function label(name: Label, count?: number): string {
    const span = qs<HTMLElement>(`[data-subtrans-label="${name}"]`, root as HTMLElement);
    if (!span) return '';
    const copy = span.cloneNode(true) as HTMLElement;
    const slot = qs<HTMLElement>('[data-slot="count"]', copy);
    if (slot && count !== undefined)
      slot.textContent = count.toLocaleString(lang === 'en' ? 'en-US' : lang);
    return copy.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  }

  function say(text: string): void {
    if (status) status.textContent = text;
  }

  function tell(text: string): void {
    if (!notice) return;
    notice.textContent = text;
    notice.hidden = text === '';
  }

  function targetName(code: string): string {
    const option = [...targetSelect.options].find((o) => o.value === code);
    return languageName(code, lang, option?.textContent?.trim() || code);
  }

  /** The language the untranslated lines are in, when the file says it. */
  function sourceName(plan: SendPlan): string | null {
    return plan.source === null ? null : languageName(plan.source, lang, plan.source);
  }

  function plan(): SendPlan | null {
    if (source === null) return null;
    return planRequest(source.cues, source.info.tracks, targetSelect.value);
  }

  /** The output is no longer what is on screen: nothing to save until it is made again. */
  function invalidate(): void {
    generation += 1;
    output = null;
    if (download) download.disabled = true;
  }

  function hideResult(): void {
    invalidate();
    shown = null;
    if (result) result.hidden = true;
    outcome?.replaceChildren();
  }

  /**
   * Translate is offered when there is a file, it fits, it has words, and nothing is in flight.
   * A file already in the target is offered too: pressing says so.
   */
  function refreshRun(): void {
    const p = plan();
    const fits =
      p !== null &&
      source !== null &&
      overLimit(source.info.cueCount, p.textBytes) === null &&
      (p.send.length > 0 || p.inTarget > 0);
    runButton.disabled = flight.busy || !fits;
  }

  /**
   * The translation in flight is for what was on screen, which has changed (another file, the
   * file read again, another target): aborted, its status line cleared, Translate offered again.
   */
  function cancelTranslation(): void {
    if (!flight.busy) return;
    flight.cancel();
    say('');
    refreshRun();
  }

  /** Everything the page held for the file on screen: a new one starts from nothing. */
  function forgetFile(): void {
    cancelTranslation();
    hideResult();
    if (source !== null) client.dispose([source.info.docId], [source.info.inputId]);
    source = null;
    pending = null;
    translations.clear();
    say('');
    tell('');
    if (parsed) parsed.hidden = true;
    refreshRun();
  }

  function workerError(err: unknown, fileName: string): void {
    if (!region) return;
    if (isVersionMismatch(err)) {
      console.error('[subtitle-translate]', err);
      showState(region, 'STALE_PAGE', {});
      return;
    }
    if (err instanceof WorkerFailure) {
      if (err.reason === 'needs-framerate' || err.reason === 'framerate-required') {
        if (fpsField) fpsField.hidden = false;
        fpsInput?.focus();
        return;
      }
      if (err.reason === 'is-archive') {
        showState(region, 'B5_ARCHIVE_HERE', {});
        return;
      }
      if (
        err.reason === 'unsupported-binary' ||
        err.reason === 'unknown-format' ||
        err.reason === 'is-container' ||
        err.reason === 'is-audio' ||
        err.reason === 'not-recognized'
      ) {
        const kind = (err.detail as { kind?: unknown } | null)?.kind;
        showState(region, 'B5_UNSUPPORTED_BINARY', typeof kind === 'string' ? { kind } : {});
        return;
      }
      if (err.reason === 'empty' || err.reason === 'no-cues') {
        showState(region, 'B5_EMPTY', {});
        return;
      }
      const detail = err.detail as { line?: number; message?: string } | null;
      showState(region, 'B5_PARSE_FAILED', {
        format: extensionOf(fileName).toUpperCase(),
        line: detail?.line === undefined ? '' : String(detail.line),
        detail: detail?.message ?? err.reason,
      });
      return;
    }
    console.error('[subtitle-translate]', err);
    showState(region, 'NETWORK', {});
  }

  /** The file as read: what it is, and its first lines. */
  function paintParsed(file: File, info: ParsedInfo, cues: readonly CueTextRow[]): void {
    if (!parsed) return;
    parsed.hidden = false;
    const set = (slot: string, value: string): void => {
      const node = qs<HTMLElement>(`[data-slot="${slot}"]`, parsed);
      if (node) node.textContent = value;
    };
    set('filename', shortFilename(file.name));
    set('format', info.format.toUpperCase());
    set('cue_count', info.cueCount.toLocaleString(lang === 'en' ? 'en-US' : lang));
    set('size', fileSize(info.byteLength));
    set('encoding', info.encoding.label);
    if (encodingSelect) encodingSelect.value = info.encoding.label;
    if (encodingPanel) encodingPanel.open = info.needsEncodingOverride;
    if (preview) {
      const lines = cues
        .filter((c) => c.text !== null)
        .slice(0, PREVIEW_CUES)
        .map((c) =>
          (c.text as string)
            .replace(/<[^>]*>/g, '')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&'),
        );
      preview.textContent = lines.join('\n\n');
    }
  }

  /** A file, read: hand its cues' texts to the page, or say why it cannot be translated. */
  async function adopt(file: File, info: ParsedInfo, mine: number): Promise<void> {
    const texts = await client.request('cueTexts', { docId: info.docId });
    if (mine !== generation) {
      client.dispose([info.docId], [info.inputId]);
      return;
    }
    // The file it replaces: the same input read in another encoding keeps its bytes; a file
    // read again at another frame rate is a new input, and the old one goes.
    if (source !== null) {
      const old = source.info;
      client.dispose([old.docId], old.inputId === info.inputId ? undefined : [old.inputId]);
    }
    source = { file, info, cues: texts.cues, encoding: info.encoding.label };
    usage.loaded(file, info.format);
    paintParsed(file, info, texts.cues);
    checkFile();
  }

  /** Whether this file can go: the limits, and whether it has anything to send. */
  function checkFile(): void {
    tell('');
    say('');
    const p = plan();
    if (source === null || p === null) return;
    const over = overLimit(source.info.cueCount, p.textBytes);
    if (over !== null) {
      tell(label(over.label, over.label === 'too-many' ? over.count : undefined));
    } else if (p.send.length === 0 && p.inTarget === 0) {
      tell(label('no-text'));
    }
    refreshRun();
  }

  async function read(file: File, mine: number): Promise<void> {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const info = await client.request('parse', {
        inputName: file.name,
        bytes,
        retainRaw: true,
        retainBytes: true,
        ...(framerate === null ? {} : { framerate }),
      });
      if (mine !== generation) {
        client.dispose([info.docId], [info.inputId]);
        return;
      }
      await adopt(file, info, mine);
    } catch (err) {
      if (mine !== generation) return;
      const asks =
        err instanceof WorkerFailure &&
        (err.reason === 'needs-framerate' || err.reason === 'framerate-required');
      if (!asks) usage.failed(file, failedFormat(extensionOf(file.name), err));
      workerError(err, file.name);
    }
  }

  async function takeFiles(files: File[]): Promise<void> {
    const file = files[0];
    if (!file) return;
    forgetFile();
    region?.replaceChildren();
    framerate = null;
    if (fpsInput) fpsInput.value = '';
    if (fpsField) fpsField.hidden = true;
    const mine = ++generation;
    const kind = classify(file);
    if (kind === 'archive') {
      usage.failed(file, 'zip');
      if (region) showState(region, 'B5_ARCHIVE_HERE', {});
      return;
    }
    if (kind !== 'subtitle') {
      usage.failed(file, failedFormat(extensionOf(file.name), null));
      if (region) showState(region, 'B5_UNSUPPORTED_BINARY', {});
      return;
    }
    pending = file;
    await read(file, mine);
  }

  /* ── translate ───────────────────────────────────────────────────────── */

  async function translate(): Promise<void> {
    const p = plan();
    const from = source;
    if (p === null || from === null || flight.busy) return;
    const target = targetSelect.value;
    if (!TARGET.test(target)) return;
    region?.replaceChildren();
    tell('');
    if (p.send.length === 0) {
      if (p.inTarget > 0) {
        say('');
        tell(
          t('This file is already in {language}.', lang).replace('{language}', targetName(target)),
        );
      } else {
        tell(label('no-text'));
      }
      return;
    }
    const kept = translations.find(p, target);
    if (kept !== null) {
      await show(kept, from);
      return;
    }

    hideResult();
    const mine = generation;
    /** Still the file and the screen this translation was asked for. */
    const current = (): boolean => mine === generation && source === from;
    say(label('running'));
    const format = usageFormat(from.info.format);
    const asked = flight.run(
      {
        queued: () => {
          if (current()) say(label('queued'));
        },
        running: () => {
          if (current()) say(label('running'));
        },
      },
      (on) =>
        translateSubtitles(
          { target, source: p.source, format: from.info.format, cues: [...p.send] },
          on,
        ),
    );
    refreshRun();
    try {
      const answer = await asked;
      // Cancelled: whatever cancelled it (cancelTranslation) has already put the page right.
      if (answer === null) return;
      const translation = translations.keep(p, target, answer);
      countUse('subtitle_translate', tool, format, {
        target,
        cues: Math.max(1, translation.texts.length),
        format,
      });
      if (!current()) return;
      await show(translation, from);
    } catch (err) {
      if (!current()) return;
      say('');
      if (err instanceof GeminiLimitError) {
        tell(limitMessage(err.limit, lang));
        countUse('gemini_limit_hit', tool, format, {
          feature: 'subtitles',
          scope: err.limit.scope,
        });
      } else if (err instanceof ApiError && err.problem.code === 'SUBTITLES_TOO_LARGE') {
        tell(label('too-much'));
      } else if (err instanceof ApiError && err.problem.code === 'ALREADY_IN_LANGUAGE') {
        // The file's own language said so (`source`): nothing to translate.
        tell(
          t('This file is already in {language}.', lang).replace('{language}', targetName(target)),
        );
      } else if (err instanceof ApiError && region) {
        showProblem(region, err.problem);
      } else {
        console.error('[subtitle-translate]', err);
        if (region) showState(region, 'NETWORK', {});
      }
    } finally {
      refreshRun();
    }
  }

  /** A translation on screen: the status, the lines left as they were, and the file made. */
  async function show(translation: Translation, from: Source): Promise<void> {
    shown = { translation, source: from };
    say(
      t('Translated into {language}.', lang).replace('{language}', targetName(translation.target)),
    );
    const p = plan();
    tell(leftNotice(translation.missing + translation.kept, p ? sourceName(p) : null, lang));
    if (formatSelect) {
      // Rebuilt rather than hidden: Safari shows a hidden <option> anyway.
      const choices = saveChoices(writes, from.info.format);
      formatSelect.replaceChildren(...formatOptions.filter((o) => choices.includes(o.value)));
      const wanted = defaultFormat(pageFormat, from.info.format, choices);
      if (wanted !== undefined) formatSelect.value = wanted;
    }
    if (result) result.hidden = false;
    await build();
  }

  /* ── the file ────────────────────────────────────────────────────────── */

  /** Make the file to save from the translation on screen, in the format and shape chosen. */
  async function build(): Promise<void> {
    invalidate();
    const now = shown;
    if (now === null || !formatSelect) return;
    const mine = generation;
    const format = formatSelect.value as WriteFormat;
    const pair = both?.checked === true;
    const { translation, source: from } = now;
    const made: DocumentId[] = [];
    try {
      const copy = await client.request('redecode', {
        inputId: from.info.inputId,
        encodingLabel: from.encoding,
      });
      made.push(copy.docId);
      const done = await client.request('translateCues', {
        docId: copy.docId,
        texts: translation.texts,
        language: translation.target,
        ...(pair ? { pair: true } : {}),
      });
      let docId = done.docId;
      if (pair && done.translationId !== null) {
        made.push(done.docId, done.translationId);
        // The merge pages' own op, as /dual-subtitles builds it: each line with its own
        // translation under it (adapter.md §3.5 says why threshold 0 meets each cue's own).
        const { ops } = opsFromValues(
          ['mergeDocuments', 'renumber'],
          { mode: 'nearest-cue', threshold: 0, lineBreakBetween: true, separateTrack: true },
          { others: [done.translationId] },
        );
        const applied = await client.request('apply', { docId, ops });
        docId = applied.docId;
      }
      const written = await client.request('write', {
        docId,
        format,
        options: {
          ...downloadWriteOptions(format, {
            framerate: writeFramerate(format, from.info.framerate, []),
          }),
          // An untranslated line (a sign, a drawing) goes back byte for byte; the translated
          // ones are stale and written from the model.
          useRawPassthrough: format === from.info.format,
          // {\an8} stays on its line in an SRT (src/core/registry/pipeline.ts keepSrtAnchors).
          anchorAsSrtTag: true,
        },
      });
      if (mine !== generation) return;
      output = {
        bytes: written.output,
        filename: downloadName(from.file.name, translation.target, written.filename, known),
        format,
      };
      if (outcome) {
        // An ASS, SSA or SRT saved as itself: what the writer reports it cannot carry (an ASS
        // `\t`, say) is on a line that was not translated (a sign the fragile-tag gate keeps),
        // and those writers put such a line out byte for byte from the file (raw passthrough),
        // so nothing is lost and nothing is said. Anywhere else it is the converter's warning,
        // as on every tool page.
        const choice =
          format === from.info.format && PASSTHROUGH.has(format)
            ? undefined
            : downloadChoices([{ format, writable: true, loss: written.loss, blocking: false }])[0];
        if (choice) renderOutcome(outcome, choice, format.toUpperCase());
        else outcome.replaceChildren();
      }
      if (download) download.disabled = false;
    } catch (err) {
      if (mine !== generation) return;
      if (err instanceof WorkerFailure && err.reason === 'blocking-loss' && outcome) {
        const blocking = (err.detail as { blocking?: readonly LossEntry[] } | null)?.blocking ?? [];
        const choice = downloadChoices([
          { format, writable: true, loss: blocking, blocking: true },
        ])[0];
        if (choice) renderOutcome(outcome, choice, format.toUpperCase());
        return;
      }
      if (
        err instanceof WorkerFailure &&
        (err.reason === 'framerate-required' || err.reason === 'needs-framerate')
      ) {
        // A frame-counted format with no rate to count in: offered only for a file that came in
        // as one (`saveChoices`), so this is a file whose rate was never known. Back to its own.
        if (formatSelect.value !== from.info.format && writes.includes(from.info.format)) {
          formatSelect.value = from.info.format;
          await build();
        }
        return;
      }
      workerError(err, from.file.name);
    } finally {
      if (made.length > 0) client.dispose(made);
    }
  }

  /* ── wiring ─────────────────────────────────────────────────────────── */

  mountDropzone(host, { onFiles: (files) => void takeFiles(files) });

  on(run, 'click', () => void translate());

  on(picker, 'change', () => {
    // Another target is another translation: the file on screen is not it, nor is the one in
    // flight.
    cancelTranslation();
    if (shown !== null && shown.translation.target !== picker.value) hideResult();
    tell('');
    say('');
    checkFile();
  });

  if (encodingSelect) {
    on(encodingSelect, 'change', () => {
      const from = source;
      if (from === null) return;
      cancelTranslation();
      hideResult();
      const mine = ++generation;
      void client
        .request('redecode', { inputId: from.info.inputId, encodingLabel: encodingSelect.value })
        .then(async (info) => {
          if (mine !== generation) {
            client.dispose([info.docId]);
            return;
          }
          await adopt(from.file, info, mine);
        })
        .catch((err: unknown) => {
          if (mine === generation) workerError(err, from.file.name);
        });
    });
  }

  if (fpsInput) {
    const answer = (): void => {
      const rate = labelled(parseFramerate(fpsInput.value.trim()));
      fpsInput.setAttribute(
        'aria-invalid',
        rate === null && fpsInput.value.trim() !== '' ? 'true' : 'false',
      );
      if (rate === null || pending === null) return;
      cancelTranslation();
      framerate = rate;
      region?.replaceChildren();
      const mine = ++generation;
      void read(pending, mine);
    };
    on(fpsInput, 'change', answer);
    on(fpsInput, 'keydown', (ev) => {
      if ((ev as KeyboardEvent).key === 'Enter') answer();
    });
  }

  if (formatSelect) on(formatSelect, 'change', () => void build());
  if (both) on(both, 'change', () => void build());

  if (download) {
    on(download, 'click', () => {
      // Synchronous from here to the anchor click (see the note at the top).
      if (output === null) return;
      saveBytes(output.bytes, output.filename, output.format);
      usage.downloaded(output.format);
    });
  }
}
